import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, History } from 'lucide-react';
import storage from '../utils/storage';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';

export default function StockManagementPage() {
  const [products, setProducts] = useState(storage.getProducts());
  const [stockHistory, setStockHistory] = useState(storage.getStockHistory());
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    quantityChange: '',
    actionType: 'increase',
    reason: '',
    notes: ''
  });

  const user = auth.getCurrentUser();

  const handleAdjustment = () => {
    const change = parseInt(adjustmentData.quantityChange);
    if (!change || change <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    const actualChange = adjustmentData.actionType === 'decrease' ? -change : change;
    const newQuantity = selectedProduct.quantity + actualChange;

    if (newQuantity < 0) {
      toast.error('Quantity cannot be negative');
      return;
    }

    // Update product
    const updatedProduct = { ...selectedProduct, quantity: newQuantity, lastUpdated: new Date().toISOString() };
    const updatedProducts = products.map(p => p.id === selectedProduct.id ? updatedProduct : p);
    setProducts(updatedProducts);
    storage.setProducts(updatedProducts);

    // Add to history
    const historyEntry = {
      id: `hist-${Date.now()}`,
      productId: selectedProduct.id,
      date: new Date().toISOString(),
      actionType: adjustmentData.reason,
      quantityChange: actualChange,
      reason: adjustmentData.reason,
      notes: adjustmentData.notes,
      user: user.username
    };
    const updatedHistory = [...stockHistory, historyEntry];
    setStockHistory(updatedHistory);
    storage.setStockHistory(updatedHistory);

    toast.success(`Stock ${adjustmentData.actionType}d successfully`);
    setShowAdjustDialog(false);
    setAdjustmentData({ quantityChange: '', actionType: 'increase', reason: '', notes: '' });
  };

  const openAdjustDialog = (product) => {
    setSelectedProduct(product);
    setShowAdjustDialog(true);
  };

  const productHistory = selectedProduct 
    ? stockHistory.filter(h => h.productId === selectedProduct.id).sort((a, b) => new Date(b.date) - new Date(a.date))
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tight">Stock Management</h1>
        <p className="text-muted-foreground mt-2">Adjust inventory levels and track stock history</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Products Inventory</CardTitle>
            <CardDescription>Select a product to adjust stock levels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {products.map(product => (
                <div key={product.id} className="flex items-center justify-between p-4 border rounded-sm hover:border-primary transition-colors">
                  <div className="flex items-center gap-3">
                    {product.image && (
                      <img src={product.image} alt={product.name} className="w-16 h-16 object-cover rounded-sm" />
                    )}
                    <div>
                      <p className="font-semibold">{product.name}</p>
                      <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-2xl font-bold text-tabular">{product.quantity}</p>
                      <p className="text-sm text-muted-foreground">{product.unit}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => openAdjustDialog(product)}
                      data-testid={`adjust-stock-${product.id}`}
                    >
                      Adjust
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History size={20} />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {stockHistory.slice(-10).reverse().map(entry => {
                const product = products.find(p => p.id === entry.productId);
                return (
                  <div key={entry.id} className="p-3 border rounded-sm text-sm">
                    <p className="font-semibold">{product?.name || 'Unknown Product'}</p>
                    <p className={`text-xs ${entry.quantityChange > 0 ? 'text-success' : 'text-destructive'}`}>
                      {entry.quantityChange > 0 ? '+' : ''}{entry.quantityChange} {product?.unit}
                    </p>
                    <p className="text-xs text-muted-foreground">{entry.reason}</p>
                    <p className="text-xs text-muted-foreground">{new Date(entry.date).toLocaleString()}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent className="w-[95vw] sm:w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black font-heading">Adjust Stock</DialogTitle>
            <DialogDescription>{selectedProduct?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-sm">
              <p className="text-sm text-muted-foreground">Current Quantity</p>
              <p className="text-3xl font-bold text-tabular">{selectedProduct?.quantity} {selectedProduct?.unit}</p>
            </div>

            <div className="space-y-2">
              <Label>Action Type</Label>
              <Select
                value={adjustmentData.actionType}
                onValueChange={(value) => setAdjustmentData({ ...adjustmentData, actionType: value })}
              >
                <SelectTrigger data-testid="adjustment-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="increase">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={16} className="text-success" />
                      Increase Stock
                    </div>
                  </SelectItem>
                  <SelectItem value="decrease">
                    <div className="flex items-center gap-2">
                      <TrendingDown size={16} className="text-destructive" />
                      Decrease Stock
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                data-testid="quantity-change-input"
                value={adjustmentData.quantityChange}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, quantityChange: e.target.value })}
                placeholder="Enter quantity"
              />
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Select
                value={adjustmentData.reason}
                onValueChange={(value) => setAdjustmentData({ ...adjustmentData, reason: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Received Stock">Received Stock</SelectItem>
                  <SelectItem value="Damage">Damage</SelectItem>
                  <SelectItem value="Theft">Theft</SelectItem>
                  <SelectItem value="Return">Return</SelectItem>
                  <SelectItem value="Correction">Correction</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={adjustmentData.notes}
                onChange={(e) => setAdjustmentData({ ...adjustmentData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowAdjustDialog(false)}>
              Cancel
            </Button>
            <Button data-testid="save-adjustment-btn" onClick={handleAdjustment}>
              Save Adjustment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
